import type { CreateResetLinkResponse, Locale, Role } from "@shelflist/shared";
import { hasRole } from "@shelflist/shared";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { KeyRound, Trash2, UserRoundCheck, UserRoundX } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  CheckboxRow,
  DeliveryAlert,
  LocaleSelect,
  useLinkEmails,
  useUiLocale,
} from "@/components/admin/link-email";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { CopyField } from "@/components/copy-field";
import { FormError } from "@/components/form-error";
import { Page } from "@/components/page";
import {
  type ResponsiveColumn,
  ResponsiveTable,
  RowActionsMenu,
} from "@/components/responsive-table";
import { RoleSelect } from "@/components/role-select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { useFormat } from "@/i18n/format";
import { api, call } from "@/lib/api";
import { useErrorMessage } from "@/lib/errors";
import { queryKeys, usersQuery } from "@/lib/queries";
import { useMe } from "@/lib/session";

export const Route = createFileRoute("/_app/admin/users")({
  // Non-admins get the NoPermission page from the layout; do not fire a request that would 403.
  loader: ({ context }) =>
    hasRole(context.me, "admin") ? context.queryClient.ensureQueryData(usersQuery) : undefined,
  component: UsersPage,
});

type User = Awaited<ReturnType<typeof usersQuery.queryFn & {}>>["users"][number];

function useUserMutations() {
  const queryClient = useQueryClient();
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.users });
    // The current admin may have changed their own role.
    await queryClient.invalidateQueries({ queryKey: queryKeys.me });
  };
  // On error the global handler shows the translated message (e.g. last_admin)
  // and the list is refetched so selects snap back to the server state.
  return {
    setRole: useMutation({
      mutationFn: ({ id, role }: { id: string; role: Role }) =>
        call(api.users[":id"].role.$put({ param: { id }, json: { role } })),
      onSettled: refresh,
    }),
    setDisabled: useMutation({
      mutationFn: ({ id, disabled }: { id: string; disabled: boolean }) =>
        disabled
          ? call(api.users[":id"].disable.$post({ param: { id } }))
          : call(api.users[":id"].enable.$post({ param: { id } })),
      onSettled: refresh,
    }),
    remove: useMutation({
      mutationFn: (id: string) => call(api.users[":id"].$delete({ param: { id } })),
      onSettled: refresh,
    }),
    // Without a body this is the one-click flow; the dialog sends `sendEmail` and `locale`.
    resetLink: useMutation({
      mutationFn: ({ id, body }: { id: string; body?: { sendEmail: boolean; locale: Locale } }) =>
        call(
          api.users[":id"]["reset-link"].$post(
            { param: { id } },
            body
              ? {
                  init: {
                    body: JSON.stringify(body),
                    headers: { "content-type": "application/json" },
                  },
                }
              : undefined,
          ),
        ) as Promise<CreateResetLinkResponse>,
    }),
  };
}

export function UsersPage() {
  const { t } = useTranslation(["admin", "common"]);
  const format = useFormat();
  const me = useMe();
  const { data } = useSuspenseQuery(usersQuery);
  const mutations = useUserMutations();
  const linkEmails = useLinkEmails();
  const [resetFor, setResetFor] = useState<{ user: User; url: string } | null>(null);
  // With link emails available the reset starts in a dialog (checkbox + language).
  const [resetDialogFor, setResetDialogFor] = useState<User | null>(null);

  // Target and open state are separate so the dialog keeps its text during the closing animation.
  const [deleting, setDeleting] = useState<User | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const askDelete = (user: User) => {
    setDeleting(user);
    setDeleteOpen(true);
  };

  const columns: ResponsiveColumn<User>[] = [
    {
      id: "user",
      header: t("users.columns.user"),
      primary: true,
      cell: (user) => (
        <>
          <div className="font-medium">
            {user.name}
            {user.id === me.id ? (
              <span className="text-muted-foreground font-normal"> ({t("users.you")})</span>
            ) : null}
          </div>
          <div className="text-muted-foreground text-sm break-all">{user.email}</div>
          <div className="text-muted-foreground mt-0.5 text-xs">
            {t("users.meta", {
              created: format.date(user.createdAt),
              lastSignIn: user.lastSignInAt ? format.dateTime(user.lastSignInAt) : t("users.never"),
            })}
          </div>
        </>
      ),
    },
    {
      id: "role",
      header: t("users.columns.role"),
      cell: (user) => (
        <RoleSelect
          value={user.role}
          aria-label={t("users.roleFor", { name: user.name })}
          className="w-32"
          disabled={mutations.setRole.isPending}
          onChange={(role) => mutations.setRole.mutate({ id: user.id, role })}
        />
      ),
    },
    {
      id: "status",
      header: t("users.columns.status"),
      cell: (user) => (
        <Badge variant={user.status === "disabled" ? "outline" : "secondary"}>
          {t(`users.status.${user.status}`)}
        </Badge>
      ),
    },
  ];

  return (
    <Page title={t("users.title")} description={t("users.description")}>
      <ResponsiveTable
        rows={data.users}
        rowKey={(user) => user.id}
        columns={columns}
        actionsHeader={t("users.columns.actions")}
        actions={(user) => {
          const isSelf = user.id === me.id;
          const disabled = user.status === "disabled";
          return (
            <RowActionsMenu name={user.name}>
              <DropdownMenuItem
                disabled={disabled || isSelf || mutations.resetLink.isPending}
                onSelect={() =>
                  linkEmails.available
                    ? setResetDialogFor(user)
                    : mutations.resetLink.mutate(
                        { id: user.id },
                        { onSuccess: (result) => setResetFor({ user, url: result.url }) },
                      )
                }
              >
                <KeyRound aria-hidden />
                {t("users.resetLink")}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={mutations.setDisabled.isPending}
                onSelect={() => mutations.setDisabled.mutate({ id: user.id, disabled: !disabled })}
              >
                {disabled ? <UserRoundCheck aria-hidden /> : <UserRoundX aria-hidden />}
                {disabled ? t("users.enable") : t("users.disable")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onSelect={() => askDelete(user)}>
                <Trash2 aria-hidden />
                {t("common:actions.delete")}
              </DropdownMenuItem>
            </RowActionsMenu>
          );
        }}
      />
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t("users.deleteTitle", { name: deleting?.name ?? "" })}
        description={t("users.deleteDescription", { email: deleting?.email ?? "" })}
        confirmLabel={t("common:actions.delete")}
        destructive
        onConfirm={() => {
          if (!deleting) return;
          const target = deleting;
          mutations.remove.mutate(target.id, {
            onSuccess: () => toast.success(t("users.deleted", { name: target.name })),
          });
        }}
      />
      <ResetLinkDialog user={resetDialogFor} onClose={() => setResetDialogFor(null)} />
      <Dialog open={resetFor !== null} onOpenChange={(open) => !open && setResetFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("users.resetTitle", { name: resetFor?.user.name ?? "" })}</DialogTitle>
            <DialogDescription>{t("users.resetDescription")}</DialogDescription>
          </DialogHeader>
          {resetFor ? <CopyField value={resetFor.url} label={t("users.resetLink")} /> : null}
          <DialogFooter>
            <Button onClick={() => setResetFor(null)}>{t("common:actions.done")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}

/** Reset link with the "also send by email" option; shown while link emails are available. */
function ResetLinkDialog({ user, onClose }: { user: User | null; onClose: () => void }) {
  const { t } = useTranslation(["admin", "common"]);
  const errorMessage = useErrorMessage();
  const uiLocale = useUiLocale();
  const mutations = useUserMutations();
  const [sendEmail, setSendEmail] = useState(true);
  const [locale, setLocale] = useState<Locale>(uiLocale);
  const [result, setResult] = useState<CreateResetLinkResponse | null>(null);
  // Counts the links created by "Send again"; remounts the polite status line so it is announced.
  const [renewals, setRenewals] = useState(0);
  // Keep the last user while the dialog closes so its text does not flicker.
  const [last, setLast] = useState<User | null>(null);
  if (user && user !== last) setLast(user);
  const target = user ?? last;
  const { mutate, isPending, error, reset } = mutations.resetLink;

  function submit(send: boolean) {
    if (!target) return;
    mutate(
      { id: target.id, body: { sendEmail: send, locale } },
      {
        onSuccess: (data) => {
          if (result) setRenewals((count) => count + 1);
          setResult(data);
        },
      },
    );
  }

  function forget() {
    // The link is shown exactly once; the next opening starts from the options.
    setResult(null);
    setRenewals(0);
    setSendEmail(true);
    setLocale(uiLocale);
    reset();
  }

  // The link exists only in this response: closing while it is created would lose it for good.
  function onOpenChange(next: boolean) {
    if (next || isPending) return;
    onClose();
  }

  return (
    <Dialog open={user !== null} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={!isPending} onCloseAutoFocus={forget}>
        {result ? (
          <>
            <DialogHeader>
              <DialogTitle>{t("users.resetTitle", { name: target?.name ?? "" })}</DialogTitle>
              <DialogDescription>{t("users.resetDescription")}</DialogDescription>
            </DialogHeader>
            {result.email ? (
              <DeliveryAlert
                result={result.email}
                success={t("users.resetSendSuccess", { email: target?.email ?? "" })}
                failed={(message) => t("users.resetSendFailed", { error: message })}
              />
            ) : null}
            <CopyField value={result.url} label={t("users.resetLink")} />
            <FormError message={error ? errorMessage(error) : null} />
            <div role="status" className="text-sm">
              {renewals > 0 ? <p key={renewals}>{t("users.resetRenewed")}</p> : null}
            </div>
            {result.email ? (
              <div className="grid gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="justify-self-start"
                  disabled={isPending}
                  onClick={() => submit(true)}
                >
                  {t("users.resetSendAgain")}
                </Button>
                <p className="text-muted-foreground text-sm">{t("users.resetSendAgainHint")}</p>
              </div>
            ) : null}
            <DialogFooter>
              <Button disabled={isPending} onClick={() => onOpenChange(false)}>
                {t("common:actions.done")}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form
            noValidate
            className="grid gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              submit(sendEmail);
            }}
          >
            <DialogHeader>
              <DialogTitle>{t("users.resetTitle", { name: target?.name ?? "" })}</DialogTitle>
              <DialogDescription>{t("users.resetDescription")}</DialogDescription>
            </DialogHeader>
            <FormError message={error ? errorMessage(error) : null} />
            <CheckboxRow
              id="reset-send"
              checked={sendEmail}
              onChange={setSendEmail}
              label={t("users.resetSendEmail", { email: target?.email ?? "" })}
            />
            {sendEmail ? (
              <LocaleSelect id="reset-locale" value={locale} onChange={setLocale} />
            ) : null}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={isPending}
                onClick={() => onOpenChange(false)}
              >
                {t("common:actions.cancel")}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending && sendEmail ? t("invitations.sending") : t("users.resetSubmit")}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
